<script setup lang="ts">
import { storeToRefs } from "pinia";
import { useCategoriesStore } from '@/stores/CategoriesStore'
import { ref } from 'vue'

import RemoteImage from '@/components/general/BaseRemoteImage.vue'
import MiButton from '@/components/buttons/MiButton.vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({
  activeTabProp: {
    type: Boolean,
    default: true
  }
});

const categoriesStore = useCategoriesStore()
const { getProductsCategories, getMixCategories } = storeToRefs(categoriesStore)
const categoryColors = ['#FCD099', '#CFE1C1', '#FCF2A6', '#FEE0E0', '#E0F4F4', '#E8F7B8', '#E8F7B8'];

const activeTab = ref(props.activeTabProp);
</script>

<template>
  <div class="w-100 px-4 lg:px-6 pt-1 relative zindex-11">
    <div class="category-head flex flex-row justify-between w-100 sticky left-0 top-0 py-3 zindex-10">
      <div class="flex items-center">
        <MiButton
          v-close-popper
          icon="mi-arrow-l"
          size="md"
          variant="text"
          rounded
          severity="primary"
          class="mr-1"
        />
        <h4 class="text-lg text-primary mb-0">
          {{ activeTab ? t('product_categories') : t('mixing_categories') }}
        </h4>
      </div>
      <button
        class="border-0 bg-transparent p-0 m-0 shadow-none text-primary-700 text-md flex items-center"
        @click="activeTab = !activeTab"
      >
        <strong class="underline mr-1">
          {{ activeTab ? t('mixing') : t('products') }}
        </strong>
        <i
          class="icon-16"
          :class="activeTab ? 'mi-mix' : 'mi-goods'" 
        />
      </button>
    </div>
    <div class="mt-2 pb-4">
      <ul
        v-if="activeTab"
        class="list-unstyled flex flex-col"
      >
        <li
          v-for="(category, index) in getProductsCategories.filter(cat => cat.subcategories.length > 0)"
          :key="index"
          class="flex flex-col relative"
        >
          <div
            class="rounded-lg mb-4"
            :style="{ backgroundColor: categoryColors[index] }"
          >
            <RouterLink
              v-close-popper
              :to="{ name: 'productsPath', params: { permalink: category.permalink } }"
              class="no-decoration font-family-secondary font-bold text-lg flex pl-2 pr-3 py-2 items-center relative stretched-link"
            >
              <div class="mr-3 col-2 py-1 bg-white rounded-lg">
                <RemoteImage
                  :src="`https://img.mix.it/general_assets/categories/${category.image}`"
                  img-class="w-100"
                  :alt="category.title"
                />
              </div>
              {{ category.title }}
            </RouterLink>
            <div class="flex flex-col px-2 pb-2">
              <ul class="list-unstyled flex flex-col w-100">
                <li
                  v-for="(subCategory, subIndex) in category.subcategories"
                  :key="subIndex"
                  class="mt-2"
                >
                  <RouterLink
                    v-close-popper
                    :to="{ name: 'productsPath', params: { permalink: subCategory.permalink } }"
                    class="subitem underline-hover text-sm px-3 py-2 inline-block rounded-lg relative stretched-link w-100 font-bold"
                    :style="{ backgroundColor: '#ffffff80' }"
                  >
                    {{ subCategory.title }}
                  </RouterLink>
                </li>
              </ul>
            </div>
          </div>
        </li>
        <li class="flex flex-col mt-1">
          <RouterLink
            v-close-popper
            :to="{ name: 'allProductsPath' }"
            class="no-decoration font-bold font-family-secondary text-primary text-lg block relative stretched-link shadow px-3 py-2 rounded-lg bg-white"
          >
            <i class="mi-goods icon-20 mr-1" />
            {{ t('show_all_products') }}
          </RouterLink>
        </li>
      </ul>
      <ul
        v-else
        class="list-unstyled flex flex-col"
      >
        <li
          v-for="(category, index) in getMixCategories"
          :key="index"
          class="flex flex-col relative"
        >
          <RouterLink
            v-close-popper
            :to="{ name: 'mixesPath', params: { permalink: category.permalink } }"
            class="no-decoration font-family-secondary font-bold text-lg flex pl-2 pr-3 py-2 rounded-lg items-center relative stretched-link mb-4"
            :style="{ backgroundColor: categoryColors[index] }"
          >
            <div class="mr-3 col-2 py-1 bg-white rounded-lg">
              <RemoteImage
                :src="`https://img.mix.it/general_assets/categories/${category.image}`"
                img-class="w-100"
                :alt="category.text"
              />
            </div>
            {{ category.title }}
          </RouterLink>
          <div
            v-if="false"
            class="flex flex-col mb-5"
          >
            <ul class="list-unstyled flex flex-col">
              <li
                v-for="(subCategory, subIndex) in category.subcategories"
                :key="subIndex"
                class="mt-2"
              >
                <RouterLink
                  v-close-popper
                  :to="{ name: 'mixesPath', params: { permalink: category.permalink } }"
                  class="subitem underline-hover text-sm px-3 py-2 inline-block rounded-lg relative stretched-link"
                  :style="{ backgroundColor: `${categoryColors[index]}80` }"
                >
                  {{ subCategory.title }}
                </RouterLink>
              </li>
            </ul>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.category-head {
    background-color: #F8F3EB;
}
.subitem {
    min-width: 208px;
}
</style>

<i18n>
  {
    "cs": {
    "mixing": "Míchání",
    "mixing_categories": "Kategorie míchání",
    "product_categories": "Kategorie produktů",
    "products": "Produkty",
    "show_all_products": "Zobrazit všechny produkty"
},
    "sk": {
    "mixing": "Miešanie",
    "mixing_categories": "Kategória miešania",
    "product_categories": "Kategória produktov",
    "products": "Produkty",
    "show_all_products": "Zobraziť všetky produkty"
},
    "pl": {
    "mixing": "Mieszanie",
    "mixing_categories": "Kategoria mieszanie",
    "product_categories": "Kategorie produktów",
    "products": "Produkty",
    "show_all_products": "Pokaż wszystkie produkty"
},
    "hu": {
    "mixing": "Keverés",
    "mixing_categories": "Keverés kategória",
    "product_categories": "Termékkategóriák",
    "products": "Termékek",
    "show_all_products": "Összes termék megjelenítése"
},
    "de": {
    "mixing": "Mixen",
    "mixing_categories": "Kategorie Mixen",
    "product_categories": "Produktkategorien",
    "products": "Produkte",
    "show_all_products": "Alle Produkte anzeigen"
},
    "at": {
    "mixing": "Mixen",
    "mixing_categories": "Kategorie Mixen",
    "product_categories": "Produktkategorien",
    "products": "Produkte",
    "show_all_products": "Alle Produkte anzeigen"
},
    "ro": {
    "mixing": "Mixare",
    "mixing_categories": "Categoria de mixare",
    "product_categories": "Categorii de produse",
    "products": "Produse",
    "show_all_products": "Afișează toate produsele"
},
    "fr": {
    "mixing": "Mix",
    "mixing_categories": "Catégorie création de mix",
    "product_categories": "Catégories de produits",
    "products": "Produits",
    "show_all_products": "Afficher tous les produits"
}
  }
</i18n>